import type { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';
import { Children, cloneElement, createElement, isValidElement } from 'react';
import { usePressFeedbackClick } from '../hooks/usePressFeedbackClick.js';
import { cx } from '../utils/cx.js';
import styles from './IconButton.module.css';

function iconChildrenWithAriaHidden(children: ReactNode): ReactNode {
  return Children.map(children, (child) => {
    if (!isValidElement<{ 'aria-hidden'?: boolean; 'aria-label'?: string }>(child)) return child;
    if (child.props['aria-hidden'] != null || child.props['aria-label'] != null) return child;
    return cloneElement(child, { 'aria-hidden': true });
  });
}

export type IconButtonVariant = 'ghost' | 'soft' | 'danger' | 'primary';

/** Default: native `<button>`. Pass `as={Link}` — `to`/`href` from host props. */
export type IconButtonProps<T extends ElementType = 'button'> = {
  as?: T;
  /** Single icon node — pair with `aria-label`. */
  children: ReactNode;
  'aria-label': string;
  variant?: IconButtonVariant;
  size?: 'xs' | 'sm' | 'md';
  /** No border — use inside `Input` suffix / tight rows so outer field owns edge. */
  borderless?: boolean;
  /**
   * When false, click runs immediately (no press-hold delay / `data-wp-pressing`).
   * Use for detail pencils and other atomic edit affordances.
   */
  pressFeedback?: boolean;
  /** Only when `as` is omitted (native `<button>`). */
  type?: HTMLButtonElement['type'];
} & Omit<ComponentPropsWithoutRef<T>, 'as' | 'children' | 'aria-label'>;

export function IconButton<T extends ElementType = 'button'>(props: IconButtonProps<T>) {
  const {
    as,
    variant = 'ghost',
    size = 'sm',
    borderless,
    className,
    type = 'button',
    children,
    disabled,
    onClick,
    pressFeedback = true,
    ...rest
  } = props;
  const Comp = (as ?? 'button') as ElementType;
  const isNativeButton = !as;
  const resolvedType = type ?? 'button';
  const { onClick: pressOnClick, pressAttrs } = usePressFeedbackClick(
    pressFeedback ? onClick : undefined,
    {
      disabled: Boolean(disabled),
      deferHostDefault: !isNativeButton,
      submitForm: isNativeButton && resolvedType === 'submit',
    },
  );

  return createElement(
    Comp,
    {
      ...(isNativeButton ? { type: resolvedType } : {}),
      className: cx(
        styles.btn,
        styles[variant],
        styles[`sz_${size}`],
        styles.focus,
        borderless && styles.borderless,
        className,
      ),
      disabled,
      onClick: pressFeedback ? pressOnClick : onClick,
      ...(pressFeedback ? pressAttrs : {}),
      ...rest,
    } as Record<string, unknown>,
    borderless ? null : <span className={styles.face} aria-hidden />,
    <span className={styles.icon}>{iconChildrenWithAriaHidden(children)}</span>,
  );
}
